/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/number/ToMilliseconds.ts
 * @create:      2025-06-27 10:43:28.161
 * @modify:      2025-07-11 21:19:12.811
 * @version:     6.0.0
 * @times:       37
 * @lines:       107
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { MillisecondsPerDay, MillisecondsPerHour, MillisecondsPerMinute, MillisecondsPerSecond, MillisecondsPerWeek } from "../const/TimeFactor";
import { ITimeSpan } from "../interface/ITimeSpan";
import { TTimeUnit } from "../type/advanced";

/**
 * Converts a time span object to its equivalent duration in milliseconds.
 *
 * @param span - The time span object to convert.
 * @returns The total number of milliseconds represented by the time span.
 * 
 * @since 4.0.0
 * @version 2025-07-09
 */
export function ToMilliseconds(span: ITimeSpan): number;
/**
 * Converts a numeric value from the specified time unit to milliseconds.
 *
 * @param num - The numeric value to convert.
 * @param currentUnit - The unit of the input value (e.g., seconds, minutes, hours).
 * @returns The equivalent value in milliseconds.
 * 
 * @since 4.0.0
 * @version 2023-11-27
 */
export function ToMilliseconds(num: number, currentUnit: TTimeUnit): number;
export function ToMilliseconds(value: number | ITimeSpan, currentUnit?: TTimeUnit): number {
    if (typeof value === "number") {
        switch (currentUnit) {
            case "milliseconds":
                return value;
            case "seconds":
                return value * MillisecondsPerSecond;
            case "minutes":
                return value * MillisecondsPerMinute;
            case "hours":
                return value * MillisecondsPerHour;
            case "days":
                return value * MillisecondsPerDay;
            case "weeks":
                return value * MillisecondsPerWeek;

            default:
                throw new Error(`Unexpected time unit: ${currentUnit}.`);
        }
    } else {
        let result = 0;
        
        if (value.milliseconds !== undefined) {
            result += value.milliseconds;
        }

        if (value.seconds !== undefined) {
            result += value.seconds * MillisecondsPerSecond;
        }

        if (value.minutes !== undefined) {
            result += value.minutes * MillisecondsPerMinute;
        }

        if (value.hours !== undefined) {
            result += value.hours * MillisecondsPerHour;
        }

        if (value.days !== undefined) {
            result += value.days * MillisecondsPerDay;
        }

        if (value.weeks !== undefined) {
            result += value.weeks * MillisecondsPerWeek;
        }

        return result;
    }
}
